import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { CalendarClock, ChevronDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSeparator,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { ActionPopover } from '@/components/action-popover'
import { useUpdateThreadFollowUp } from '@/features/communications/hooks'
import type { POCommunicationThread } from '@/features/communications/types'
import { cn } from '@/lib/utils'
import { followUpLabel, formatDay, type FollowUpTone } from './follow-up-dates'

export type CheckIn = POCommunicationThread['upcoming_check_ins'][number]

const TONES: Record<FollowUpTone, string> = {
  upcoming:
    'bg-sky-50 text-sky-800 ring-sky-200 hover:bg-sky-100 dark:bg-sky-950/50 dark:text-sky-200 dark:ring-sky-900 dark:hover:bg-sky-950',
  today:
    'bg-amber-50 text-amber-900 ring-amber-200 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-200 dark:ring-amber-900 dark:hover:bg-amber-950',
  overdue:
    'bg-red-50 text-red-800 ring-red-200 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-200 dark:ring-red-900 dark:hover:bg-red-950',
  none: 'bg-muted/60 text-muted-foreground ring-border hover:bg-muted hover:text-foreground',
}

function checkInSummary(checkIn: CheckIn): string {
  if (checkIn.kind === 'pre_ship' && checkIn.ship_date) {
    return `Confirm it still ships ${formatDay(checkIn.ship_date)}`
  }
  if (checkIn.kind === 'ship_date_request') return 'Ask for a ship date'
  return checkIn.reason
}

/** The supplier check-ins planned on this thread (USA-1194), soonest first. */
function CheckInsControl({ checkIns }: { checkIns: CheckIn[] }) {
  const [open, setOpen] = useState(false)
  const next = checkIns[0]
  if (!next) return null
  return (
    <PickerMenu open={open} onOpenChange={setOpen}>
      <PickerMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-1 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Check-in {formatDay(next.due_on)}
          {checkIns.length > 1 ? ` +${checkIns.length - 1}` : ''}
          <ChevronDown aria-hidden className="h-3 w-3" />
        </button>
      </PickerMenuTrigger>
      <PickerMenuContent title="Upcoming check-ins" className="w-72">
        <PickerMenuList>
          <ol className="space-y-2 px-2 py-1.5">
            {checkIns.map((checkIn) => (
              <li key={checkIn.uuid} className="text-sm">
                <div className="font-medium">{formatDay(checkIn.due_on)}</div>
                <div className="text-muted-foreground">{checkInSummary(checkIn)}</div>
              </li>
            ))}
          </ol>
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}

const SNOOZES = [
  [1, 'Snooze 1 day'],
  [3, 'Snooze 3 days'],
  [7, 'Snooze 1 week'],
] as const

function FollowUpControl({
  threadUuid,
  followUpAt,
  reason,
  count,
  max,
}: {
  threadUuid: string
  followUpAt: string | null
  reason?: string | null
  count: number
  max: number
}) {
  const mutation = useUpdateThreadFollowUp()
  const [showCustom, setShowCustom] = useState(false)
  const [customDate, setCustomDate] = useState('')
  const customOpening = useRef(false)
  const [menuOpen, setMenuOpen] = useState(false)
  // A pick closes the menu (the phone sheet does not close itself), then acts.
  const pick = (act: () => void) => {
    setMenuOpen(false)
    act()
  }

  const snooze = (days: number) => {
    const d = new Date()
    d.setDate(d.getDate() + days)
    const iso = d.toISOString().slice(0, 10)
    mutation.mutate(
      { threadUuid, body: { follow_up_at: iso } },
      { onSuccess: () => toast.success('Follow-up rescheduled') },
    )
  }

  const today = new Date().toISOString().slice(0, 10)
  const label = followUpLabel(followUpAt)

  return (
    <ActionPopover
      anchorOnly
      open={showCustom}
      onOpenChange={(open) => {
        customOpening.current = false
        setShowCustom(open)
      }}
      title="Reschedule follow-up"
      confirmLabel="Save"
      canConfirm={Boolean(customDate)}
      pending={mutation.isPending}
      pendingLabel="Saving..."
      onConfirm={(_note, close) =>
        mutation.mutate(
          { threadUuid, body: { follow_up_at: customDate } },
          {
            onSuccess: () => {
              close()
              toast.success('Follow-up rescheduled')
            },
          },
        )
      }
      trigger={
        <span className="inline-flex min-w-0">
          <PickerMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <PickerMenuTrigger asChild>
              <button
                type="button"
                data-testid="thread-follow-up"
                data-tone={label.tone}
                title="Change follow-up"
                className={cn(
                  'inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md px-2 py-1 text-left text-xs font-medium ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  TONES[label.tone],
                )}
              >
                <CalendarClock aria-hidden className="h-3.5 w-3.5 shrink-0" />
                <span>{label.text}</span>
                <span className="font-normal opacity-75">
                  {followUpAt ? `${count} of ${max} sent` : 'Set one'}
                </span>
                <ChevronDown aria-hidden className="h-3 w-3 shrink-0 opacity-75" />
              </button>
            </PickerMenuTrigger>
            {/* Custom date opens the popover; keep focus there instead of back on the pill. */}
            <PickerMenuContent
              title={followUpAt ? `Follow-up on ${formatDay(followUpAt)}` : 'Follow-up'}
              onCloseAutoFocus={(event) => customOpening.current && event.preventDefault()}
            >
              {followUpAt && reason ? (
                <div className="max-w-72 px-3 pb-1.5 sm:px-2">
                  <p className="line-clamp-3 text-sm text-muted-foreground" title={reason}>
                    {reason}
                  </p>
                </div>
              ) : null}
              <PickerMenuList>
                {SNOOZES.map(([days, text]) => (
                  <PickerMenuItem key={days} onSelect={() => pick(() => snooze(days))}>
                    {text}
                  </PickerMenuItem>
                ))}
                <PickerMenuItem
                  onSelect={() =>
                    pick(() => {
                      customOpening.current = true
                      setCustomDate(followUpAt ?? '')
                      setShowCustom(true)
                    })
                  }
                >
                  Custom date...
                </PickerMenuItem>
                {followUpAt && (
                  <>
                    <PickerMenuSeparator />
                    <PickerMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() =>
                        pick(() =>
                          mutation.mutate(
                            { threadUuid, body: { clear: true } },
                            { onSuccess: () => toast.success('Follow-up disabled') },
                          ),
                        )
                      }
                    >
                      Disable follow-up
                    </PickerMenuItem>
                  </>
                )}
              </PickerMenuList>
            </PickerMenuContent>
          </PickerMenu>
        </span>
      }
    >
      <Input
        type="date"
        aria-label="Follow-up date"
        value={customDate}
        min={today}
        onChange={(e) => setCustomDate(e.target.value)}
        className="h-8 text-sm"
      />
    </ActionPopover>
  )
}

/**
 * The thread's next follow-up and planned supplier check-ins, shown under its latest email.
 * Follow-ups apply while the thread is waiting on the other side (sent / awaiting reply).
 */
export function ThreadFollowUp({
  threadUuid,
  status,
  followUpAt,
  reason,
  count,
  max,
  checkIns,
  className,
}: {
  threadUuid: string
  status: string
  followUpAt: string | null
  reason?: string | null
  count: number
  max: number
  checkIns: CheckIn[]
  className?: string
}) {
  const followsUp = status === 'sent' || status === 'awaiting_reply'
  if (!followsUp && checkIns.length === 0) return null
  return (
    <div className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1', className)}>
      {followsUp && (
        <FollowUpControl
          threadUuid={threadUuid}
          followUpAt={followUpAt}
          reason={reason}
          count={count}
          max={max}
        />
      )}
      {followsUp && checkIns.length > 0 && (
        <span aria-hidden className="text-xs text-muted-foreground">
          ·
        </span>
      )}
      <CheckInsControl checkIns={checkIns} />
    </div>
  )
}
